/**
 * FeesAndPaymentSection
 *
 * The "Fees and payment" section of DieticianProfileForm (design-patient-payment-guidance §4.13.4,
 * handoff Variant C): seven fields and a live preview rendered through the generator twin. Every
 * field is optional; a blank section means the platform default (D5, D6).
 *
 * @module components/dietician-registration/FeesAndPaymentSection
 */

import React from 'react';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { TextArea } from '@/components/ui/TextArea';
import { ButtonGroup } from '@/components/ui/ButtonGroup';
import { CheckboxGroup } from '@/components/ui/CheckboxGroup';
import { PaymentGuidance } from '@/components/payment-guidance';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';
import { generatePaymentGuidance, PLATFORM_DEFAULT_POLICY } from '@/utils/paymentGuidance';
import { feeFieldsToPolicy, isBlankFeeFields, type FeePolicyFields } from '@/utils/feePolicy';
import type { BillingMethod } from '@/types/paymentGuidance';

export interface FeesAndPaymentSectionProps {
  fields: FeePolicyFields;
  /** Keyed `feePolicy.<field>` (form validation and mapped server errors) */
  errors: Record<string, string>;
  disabled: boolean;
  mode: 'registration' | 'edit';
  onChange: (field: keyof FeePolicyFields, value: string | boolean) => void;
  /** Marks the field touched and validates it (the form's handleFieldBlur) */
  onBlur?: (field: keyof FeePolicyFields) => void;
  onReset: () => void;
}

const MEDICARE_OPTION = 'Chronic Condition Management Plan rebate applies';

const BILLING_OPTIONS = [
  { value: 'hicaps', label: 'Claims on the spot (HICAPS)', description: 'Health fund card swiped on the day; patient pays the gap' },
  { value: 'bill_in_full', label: 'Pays in full on the day', description: 'Patient pays, then claims any rebate afterwards' },
  { value: 'bulk_bill', label: 'Bulk bills', description: 'Nothing to pay with a valid GP plan' },
  { value: 'other', label: 'Other', description: 'Explain in the notes below' },
];

export const FeesAndPaymentSection: React.FC<FeesAndPaymentSectionProps> = ({
  fields,
  errors,
  disabled,
  mode,
  onChange,
  onBlur,
  onReset,
}) => {
  const { isDark } = useTheme();
  const blank = isBlankFeeFields(fields);
  const guidance = blank
    ? generatePaymentGuidance(PLATFORM_DEFAULT_POLICY, 'default')
    : generatePaymentGuidance(feeFieldsToPolicy(fields), 'dietitian');
  const dollar = <span className="text-sm text-secondary-300">$</span>;
  const errorOf = (field: keyof FeePolicyFields) => errors[`feePolicy.${field}`] || undefined;
  const stateOf = (field: keyof FeePolicyFields) => (errorOf(field) ? 'error' : 'default');

  return (
    <div className="space-y-4">
      <div>
        <h3 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-gray-900')}>Fees and payment</h3>
        <p className="mt-1 text-sm text-secondary-300">
          {mode === 'registration'
            ? 'Optional — you can set this later from your profile. Until you do, patients see the standard BiteWise fee text.'
            : 'What patients read about paying for their appointment. Saving makes it live straight away.'}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-5">
        <div className="md:col-span-3">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label="Initial consult fee" error={errorOf('initialFee')} helperText="Leave blank to show the standard $120">
              <Input
                type="text"
                inputMode="decimal"
                leftIcon={dollar}
                placeholder="120"
                value={fields.initialFee}
                onChange={(e) => onChange('initialFee', e.target.value)}
                onBlur={() => onBlur?.('initialFee')}
                disabled={disabled}
                state={stateOf('initialFee')}
                aria-label="Initial consult fee"
              />
            </FormField>

            <FormField label="Follow-up fee" error={errorOf('followUpFee')} helperText="Blank means the same as the initial fee">
              <Input
                type="text"
                inputMode="decimal"
                leftIcon={dollar}
                placeholder="95"
                value={fields.followUpFee}
                onChange={(e) => onChange('followUpFee', e.target.value)}
                onBlur={() => onBlur?.('followUpFee')}
                disabled={disabled}
                state={stateOf('followUpFee')}
                aria-label="Follow-up fee"
              />
            </FormField>

            <div className="md:col-span-2">
              <ButtonGroup
                label="How patients pay on the day"
                orientation="horizontal"
                maxColumns={2}
                options={BILLING_OPTIONS}
                value={fields.billingMethod}
                onChange={(value) => onChange('billingMethod', String(value) as BillingMethod)}
                disabled={disabled}
                error={errorOf('billingMethod')}
                showDescriptions
                name="billingMethod"
              />
            </div>

            <div className="md:col-span-2">
              <CheckboxGroup
                label="Medicare"
                options={[MEDICARE_OPTION]}
                selectedValues={fields.medicareApplies ? [MEDICARE_OPTION] : []}
                onChange={(_value, checked) => onChange('medicareApplies', checked)}
                helperText="Controls whether patients see the Medicare rebate line"
                state={stateOf('medicareApplies')}
                error={errorOf('medicareApplies')}
                className={cn(disabled && 'pointer-events-none opacity-60')}
              />
            </div>

            <FormField label="Cancellation notice" error={errorOf('noticeHours')} helperText="Hours. Blank shows the standard 24">
              <Input
                type="text"
                inputMode="numeric"
                rightIcon={<span className="text-sm text-secondary-300">hours</span>}
                placeholder="24"
                value={fields.noticeHours}
                onChange={(e) => onChange('noticeHours', e.target.value)}
                onBlur={() => onBlur?.('noticeHours')}
                disabled={disabled}
                state={stateOf('noticeHours')}
                aria-label="Cancellation notice"
              />
            </FormField>

            <FormField label="Late-cancel or no-show fee" error={errorOf('lateFee')} helperText="Stated to patients. Charged by your practice, not BiteWise">
              <Input
                type="text"
                inputMode="decimal"
                leftIcon={dollar}
                placeholder="0"
                value={fields.lateFee}
                onChange={(e) => onChange('lateFee', e.target.value)}
                onBlur={() => onBlur?.('lateFee')}
                disabled={disabled}
                state={stateOf('lateFee')}
                aria-label="Late-cancel or no-show fee"
              />
            </FormField>

            <div className="md:col-span-2">
              <FormField
                label="Anything else patients should know"
                error={errorOf('notes')}
                helperText="Plain language. Shown to patients word for word, before the cancellation line"
              >
                <TextArea
                  rows={3}
                  placeholder="e.g. Concession card holders receive a reduced fee — mention it when booking."
                  value={fields.notes}
                  onChange={(e) => onChange('notes', e.target.value)}
                onBlur={() => onBlur?.('notes')}
                  disabled={disabled}
                  aria-label="Anything else patients should know"
                  aria-invalid={errorOf('notes') ? true : undefined}
                />
              </FormField>
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="md:sticky md:top-6">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-xs font-medium uppercase tracking-wider text-secondary-300">What your patients will read</p>
              {!blank && (
                <button
                  type="button"
                  onClick={onReset}
                  disabled={disabled}
                  className={cn('text-xs font-medium', isDark ? 'text-primary-300 hover:text-primary-200' : 'text-primary-500 hover:text-primary-600')}
                >
                  Use the standard text
                </button>
              )}
            </div>
            <PaymentGuidance compact guidance={guidance} />
            <p className="mt-2 text-xs text-secondary-300">
              {blank
                ? 'Nothing set — this is the standard BiteWise fee text. Fill in any field to replace it.'
                : 'Live for your patients as soon as you save. Shown on referral pages and their dashboard.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeesAndPaymentSection;
